/**
 * @prettier
 */

import React, { useCallback, useEffect, useState } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _values from 'lodash/values';
import { Banner, ButtonV2, Link } from '@zola/zola-ui';
import Loader from 'components/common/ui/Loader';
import { SETTINGS_BY_CARD_TYPE } from 'cards/constants/CardTypes';
import { dateIsComplete, getValidatedDate } from '../util/helpers';
import { useDebounce, useDateSetter, useMobileCheck, usePhotoCache } from '../util/hooks';
import { createAccount, saveSingleSampleFormAsDraft } from '../util/api';
import Layout from '../Layout/Layout';
import styles from './styles.module.less';

const ExitOverlay = ({
  cardType,
  cardSuiteDetails,
  overrideActiveVariationUUID,
  isMagnet,
  form,
  flowUUID,
  step,
  isGuest,
  userContext,
  fetchUserContext,
  updateForm,
  showExitOverlay,
  setModalOptions,
  openModal,
  closeModal,
}) => {
  const cardSettings = _values(SETTINGS_BY_CARD_TYPE).find(setting => setting.type === cardType);

  const [localForm, setLocalForm] = useState({
    weddingDetails: { ...form.weddingDetails },
    account: { ...form.account },
  });

  const [errors, setErrors] = useState(null);

  const { email, password } = localForm.account;

  const { fullName, partnerFullName, stillDeciding, date } = localForm.weddingDetails;

  const [loading, setLoading] = useState(false);

  const [creatingAccount, setCreatingAccount] = useState(false);

  const [draftSaved, setDraftSaved] = useState(false);

  const photoUploadHandler = usePhotoCache();

  const isMobile = useMobileCheck();

  const dateIsValid = dateIsComplete(date);

  const setWeddingDetailsField = useCallback(
    (key, value) =>
      setLocalForm({ ...localForm, weddingDetails: { ...localForm.weddingDetails, [key]: value } }),
    [localForm, setLocalForm]
  );

  const setAccountField = useCallback(
    (key, value) => setLocalForm({ ...localForm, account: { ...localForm.account, [key]: value } }),
    [localForm, setLocalForm]
  );

  const setDate = useDateSetter(value => setWeddingDetailsField('date', value));

  const onLogin = useCallback(() => {
    const savedForm = _cloneDeep(form);

    savedForm.account.password = null;

    closeModal();
    setTimeout(() => {
      window.zolaNav.openLoginModal(() => {
        fetchUserContext().then(() => {
          openModal({ initialForm: savedForm });
          setModalOptions({ hideClose: showExitOverlay });
        });
      });
    }, 50);
  }, [form, flowUUID, step, fetchUserContext]);

  const submit = useCallback(() => {
    if (!creatingAccount && isGuest && !draftSaved) {
      setCreatingAccount(true);
      createAccount(form.account, form.weddingDetails, cardType).then(result => {
        setCreatingAccount(false);
        if (result.errors) {
          setErrors(result.errors);
          return;
        }

        fetchUserContext();
      });
    }
  }, [creatingAccount, setCreatingAccount, isGuest, draftSaved, form, cardType]);

  // Uncheck "We're still deciding" checkbox on mobile to avoid resizing deadlocks.
  useEffect(() => {
    if (isMobile && stillDeciding) {
      setWeddingDetailsField('stillDeciding', false);
    }
  }, [isMobile, setWeddingDetailsField]);

  // Once the local form has stopped being updated for 1 sec, update parent form. We never save invalid dates.
  useDebounce(
    () => {
      updateForm({
        weddingDetails: { ...localForm.weddingDetails, date: getValidatedDate(date) },
        account: { ...localForm.account },
      });
    },
    1000,
    [localForm.weddingDetails, localForm.account, date]
  );

  const [wasGuestOnExit, setWasGuestOnExit] = useState(false);

  useEffect(() => {
    if (isGuest) {
      setWasGuestOnExit(true);
    }
  }, [isGuest]);

  const closeCallback = useCallback(() => {
    if (wasGuestOnExit) {
      window.location.reload();
    }
  }, [wasGuestOnExit]);

  useEffect(() => {
    if (!isGuest && !draftSaved) {
      setLoading(true);
      saveSingleSampleFormAsDraft(
        cardSuiteDetails,
        form,
        userContext,
        photoUploadHandler,
        overrideActiveVariationUUID,
        isMagnet
      ).then(() => {
        setDraftSaved(true);
        setLoading(false);
        setModalOptions({ hideClose: false, preventClose: false, closeCallback });
      });
    }
  }, [isGuest, draftSaved]);

  if (loading) {
    return <Loader />;
  }

  if (draftSaved) {
    return (
      <div className={styles.savedBox}>
        <div className={styles.centerText}>
          <Layout.Header>We Saved Your Work</Layout.Header>
          <Layout.Text>
            Visit “Your Orders + Drafts” to continue customizing your sample anytime.
          </Layout.Text>
        </div>
        <div className={styles.buttonsRow}>
          <Link href="/wedding-planning/projects/drafts">
            <ButtonV2 variant="secondary">View Drafts</ButtonV2>
          </Link>
          <Link href={`/wedding-planning/${cardSettings.urlSlug}/shop`}>
            <ButtonV2 variant="primary">View {cardSettings.label}</ButtonV2>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Layout.Form onSubmit={submit}>
      <div className={styles.formBox}>
        <div>
          <div className={styles.col}>
            <div className={styles.centerText}>
              <Layout.Header>Want to dive back in later?</Layout.Header>
              <Layout.Text>
                Create an account to save and edit your draft anytime. Already have an account?{' '}
                <Link role="button" onClick={onLogin}>
                  Log in
                </Link>
                .
              </Layout.Text>
            </div>
            {errors && errors.weddingDetails && (
              <Banner type="error">{errors.weddingDetails}</Banner>
            )}
            {errors && errors.account && <Banner type="error">{errors.account}</Banner>}
            <div className={styles.namesBox}>
              <Layout.InputGroup>
                <Layout.Field
                  required
                  type="text"
                  label="Your Full Name"
                  disabled={!isGuest}
                  input={{
                    name: 'fullName',
                    value: fullName || '',
                    onChange: e => setWeddingDetailsField('fullName', e.target.value),
                  }}
                />
                <Layout.Spacer />
                <Layout.Field
                  required
                  type="text"
                  label="Your Partner's Full Name"
                  disabled={!isGuest}
                  input={{
                    name: 'partnerFullName',
                    value: partnerFullName || '',
                    onChange: e => setWeddingDetailsField('partnerFullName', e.target.value),
                  }}
                />
              </Layout.InputGroup>
            </div>
            <Layout.InputGroup>
              <Layout.DateField
                label="Wedding Date"
                placeholder="yyyy-mm-dd"
                disabled={stillDeciding}
                dateIsValid={dateIsValid}
                inputName="date"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
              <Layout.Spacer hideOnMobile />
              <div className={styles.checkbox}>
                <Layout.Checkbox
                  id="still-deciding"
                  label="We're still deciding"
                  checked={stillDeciding}
                  onChange={checked => setWeddingDetailsField('stillDeciding', checked)}
                />
              </div>
            </Layout.InputGroup>
            <Layout.Field
              required
              type="text"
              label="Email"
              input={{
                name: 'email',
                value: email || '',
                onChange: e => setAccountField('email', e.target.value),
              }}
            />
            <Layout.Field
              required
              type="password"
              label="Password"
              input={{
                name: 'password',
                value: password || '',
                onChange: e => setAccountField('password', e.target.value),
              }}
            />
            <div className={styles.buttonsCol}>
              <ButtonV2
                className={styles.signupButton}
                disabled={
                  !(fullName && form.weddingDetails.fullName === fullName) ||
                  !(partnerFullName && form.weddingDetails.partnerFullName === partnerFullName) ||
                  !(email && form.account.email === email) ||
                  !(password && form.account.password === password) ||
                  !dateIsValid
                }
                variant="primary"
                type="submit"
                onClick={submit}
              >
                Sign Up
              </ButtonV2>
              <Link className={styles.closeLink} role="button" onClick={closeModal}>
                Close Without Saving
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout.Form>
  );
};

export default ExitOverlay;



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/ExitOverlay/ExitOverlay.jsx